import React from 'react';
import { 
  Box, 
  VStack, 
  Text, 
  Link, 
  HStack,
  Container,
  Grid,
  GridItem,
  Image,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from '@chakra-ui/icons';

const PhotoGallery = () => (
  <Box 
    position="relative" 
    height="90px" 
    mb={8}
    width="100%"
    overflow="visible"
  >
    <HStack 
      spacing={1} 
      alignItems="flex-start"
      width="fit-content"
    >
      {[
        "/mirror-selfie.jpg",
        "/cappadocia-terrace.jpg",
        "/cafe-portrait.jpg",
        "/italian-alley.jpg",
      ].map((src, index) => (
        <Box
          key={index}
          width="75px"
          height="90px"
          borderRadius="md"
          overflow="hidden"
          transform={`rotate(${index * 2 - 3}deg)`}
          transition="all 0.2s"
          _hover={{ 
            transform: "rotate(0deg) translateY(-3px)",
            zIndex: 1,
          }}
          boxShadow="sm"
        >
          <Image 
            src={src} 
            alt={`Gallery image ${index + 1}`}
            width="100%"
            height="100%"
            objectFit="cover"
          />
        </Box>
      ))}
    </HStack>
  </Box>
);

const Section = ({ children, title, ...props }) => (
  <Box {...props}>
    {title && (
      <Text 
        fontSize="xs" 
        color="#6366f1"
        textTransform="uppercase" 
        letterSpacing="0.05em" 
        mb={4}
        fontWeight="500"
        opacity="0.8"
      >
        {title}
      </Text>
    )}
    {children}
  </Box>
);

const Home = () => {
  return (
    <Box minH="100vh" display="flex" alignItems="center" bg="white">
      <Container maxW="4xl" py={16}>
        <PhotoGallery />
        {/* Name & Intro - Full Width */}
        <Box mb={6}>
          <Text fontSize="3xl" fontWeight="500" letterSpacing="-0.03em">
            Selin Kocalar
          </Text>
        </Box>

        {/* Two Column Layout */}
        <Grid templateColumns={["1fr", "1fr", "1fr 1fr"]} gap={12}>
          {/* Left Column */}
          <GridItem>
            <VStack align="stretch" spacing={12}>
              <Section title="currently">
                <VStack align="start" spacing={3}>
                  <Link href="https://delve.co" isExternal _hover={{ textDecoration: 'none' }}>
                    <Text fontSize="sm" fontWeight="400" display="flex" alignItems="center">
                      building Delve to automate compliance with AI
                      <ExternalLinkIcon boxSize={3} ml={1} />
                    </Text>
                  </Link>
                  <Text fontSize="xs" color="gray.500">
                    on a mission to free up time for the 1% of the world that works on compliance every day
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    helping 150+ companies, backed by Y Combinator, featured in Business Insider
                  </Text>
                </VStack>
              </Section>

              <Section title="interests">
                <VStack align="start" spacing={5}>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">running</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>currently training for the Eugene Marathon</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">reading</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>Extreme Ownership, Made to Stick, Unbroken are among my favorites</Text>
                  </Box>
                </VStack>
              </Section>
            </VStack>
          </GridItem>

          {/* Right Column */}
          <GridItem>
            <VStack align="stretch" spacing={12}>
              <Section title="previously">
                <VStack align="start" spacing={5}>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">sent an experiment to the ISS</Text>
                    <Link href="https://youtu.be/Blt-kifw8BI?feature=shared" isExternal fontSize="xs" color="gray.500" mt={1}>
                      <Text display="inline">experiment conducted by astronaut Bob Hines</Text>
                      <ExternalLinkIcon boxSize={3} mx="2px" />
                    </Link>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">studied AI at MIT</Text>
                    <Link href="https://prod.so" isExternal fontSize="xs" color="gray.500" mt={1}>
                      <Text display="inline">got involved w Prod.so</Text>
                      <ExternalLinkIcon boxSize={3} mx="2px" />
                    </Link>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">worked in labs like Feng Zhang's CRISPR lab</Text>
                    <Link href="https://scholar.google.com/citations?hl=en&view_op=list_works&gmla=AOv-ny_AZ10Wi4H9lXlTmwIDgGnbUIO-GHdyYFJYyNZZqqYhbT_2WzHykI54PCydH1dz6NPiCQsN9xSnV09Hyg&user=lQxjigwAAAAJ" isExternal fontSize="xs" color="gray.500" mt={1}>
                      <Text display="inline">published 8 papers by age 20</Text>
                      <ExternalLinkIcon boxSize={3} mx="2px" />
                    </Link>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="400">built Woolly Wishes nonprofit</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>donated 6000 handmade items to refugees</Text>
                  </Box>
                </VStack>
              </Section>

              <Section title="contact">
                <HStack spacing={3} fontSize="sm" color="gray.700">
                  <Link href="https://www.linkedin.com/in/selin-kocalar/" isExternal fontWeight="400">linkedin</Link>
                  <Text color="gray.400">•</Text>
                  <Link href="https://x.com/kocalars" isExternal fontWeight="400">twitter</Link>
                  <Text color="gray.400">•</Text>
                  <Link href="mailto:selin@delve.co" fontWeight="400">email</Link>
                </HStack>
              </Section>
            </VStack>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home; 